import React, { useState } from 'react';
import { mutate as swrMutate } from 'swr';
import GenericModulePage from '../../components/generic/GenericModulePage';
import { initializeModule } from '../../utils/configInit';
import { complaintConfig } from './complaintConfig';
import { createComplaintValidationSchema } from './complaintValidation';
import { useEntities, useCreateEntity, useUpdateEntity, useDeleteEntity, fetcher, useFlexibleList } from '../../hooks/useSWR';
import ComplaintTypeSelector from './components/ComplaintTypeSelector';

/**
 * Initialize the Complaint module using generic utilities
 */
const { config: initializedConfig } = initializeModule(
  complaintConfig,
  createComplaintValidationSchema
);

/**
 * Custom ComplaintPage component with custom onSave handler for complaintActionTaken
 */
const ComplaintPage = () => {
  // State for type selection modal
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  
  // Get hooks for data operations
  const { mutate } = useEntities(initializedConfig.api.baseEndpoint);
  const createEntity = useCreateEntity(initializedConfig.api.baseEndpoint);
  const updateEntity = useUpdateEntity(initializedConfig.api.baseEndpoint);
  
  // Get complaint types for the selector
  const { data: complaintTypes = [] } = useFlexibleList(
    initializedConfig.api.lookupEndpoints?.complaintTypes || '/complaint-type'
  );
  
  // SWR hooks for complaint action taken operations
  const createActionTaken = useCreateEntity('/complaint-action-taken');
  const updateActionTaken = useUpdateEntity('/complaint-action-taken');
  const deleteActionTaken = useDeleteEntity('/complaint-action-taken');
  const { mutate: mutateActionTaken } = useEntities('/complaint-action-taken');
  
  // SWR hooks for complaint action operations
  const createAction = useCreateEntity('/complaint-action');
  const updateAction = useUpdateEntity('/complaint-action');
  const deleteAction = useDeleteEntity('/complaint-action');
  const { mutate: mutateAction } = useEntities('/complaint-action');
  
  // Helper function to fetch actions taken by complaint ID using SWR fetcher
  // This uses the same fetcher as SWR hooks for consistency
  const fetchActionsTakenByComplaint = async (complaintId) => {
    const endpoint = `/complaint-action-taken/complaint/${complaintId}`;
    try {
      // Use SWR's global mutate to get from cache or fetch using the same fetcher
      const data = await swrMutate(endpoint, () => fetcher(endpoint), false);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('Failed to fetch existing actions taken:', error);
      return [];
    }
  };
  
  // Helper function to fetch actions by complaint ID using SWR fetcher
  const fetchActionsByComplaint = async (complaintId) => {
    const endpoint = `/complaint-action/complaint/${complaintId}`;
    try {
      // Use SWR's global mutate to get from cache or fetch using the same fetcher
      const data = await swrMutate(endpoint, () => fetcher(endpoint), false);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('Failed to fetch existing actions:', error);
      return [];
    }
  };
  
  const customOnSave = async (formData, formMode, currentData, setData) => {
    console.log('🚀 customOnSave CALLED with formData keys:', Object.keys(formData));
    console.log('🚀 customOnSave - formData.complaintActionTaken:', formData.complaintActionTaken);
    console.log('🚀 customOnSave - formData.complaintActionTaken type:', typeof formData.complaintActionTaken);
    console.log('🚀 customOnSave - formData.complaintActionTaken isArray:', Array.isArray(formData.complaintActionTaken));
    
    try {
      // Extract complaintActionTaken from formData before any transformations
      // IMPORTANT: Extract this FIRST before any data cleaning/transformation
      // Also check if it's an array and has valid data
      let complaintActionTaken = formData.complaintActionTaken;
      
      // If complaintActionTaken is undefined or null, try to get it from the sync function
      if (!complaintActionTaken && typeof window !== 'undefined' && window.__getComplaintActionTaken) {
        const syncData = window.__getComplaintActionTaken();
        if (syncData && syncData.localActionsTaken && Array.isArray(syncData.localActionsTaken)) {
          console.log('🔄 Got complaintActionTaken from sync function:', syncData.localActionsTaken);
          complaintActionTaken = syncData.localActionsTaken;
        }
      }
      
      // Extract complaintActions from formData before any transformations
      let complaintActions = formData.complaintActions;
      
      // If complaintActions is undefined or null, try to get it from the sync function
      if (!complaintActions && typeof window !== 'undefined' && window.__getComplaintActions) {
        const syncData = window.__getComplaintActions();
        if (syncData && syncData.localActions && Array.isArray(syncData.localActions)) {
          console.log('🔄 Got complaintActions from sync function:', syncData.localActions);
          complaintActions = syncData.localActions;
        }
      }
      
      console.log('🔍 STEP 1 - Raw complaintActionTaken from formData:', complaintActionTaken);
      console.log('🔍 STEP 1 - Raw complaintActions from formData:', complaintActions);
      console.log('🔍 STEP 1 - Is array?', Array.isArray(complaintActionTaken));
      console.log('🔍 STEP 1 - Type:', typeof complaintActionTaken);
      
      // Ensure it's an array
      if (!Array.isArray(complaintActionTaken)) {
        console.warn('⚠️ complaintActionTaken is not an array, converting to empty array');
        complaintActionTaken = [];
      }
      
      // Ensure complaintActions is an array
      if (!Array.isArray(complaintActions)) {
        console.warn('⚠️ complaintActions is not an array, converting to empty array');
        complaintActions = [];
      }
      
      console.log('🔍 STEP 2 - Before filtering, complaintActionTaken length:', complaintActionTaken.length);
      console.log('🔍 STEP 2 - Before filtering, complaintActions length:', complaintActions.length);
      console.log('🔍 STEP 2 - Before filtering, items:', complaintActionTaken);
      
      // Filter out any invalid entries (must have at least description)
      // Also ensure each action has the required structure
      complaintActionTaken = complaintActionTaken
        .filter(action => {
          const isValid = action && 
            action.description && 
            typeof action.description === 'string' &&
            action.description.trim() !== '';
          if (!isValid) {
            console.warn('⚠️ Filtering out invalid action taken:', action);
          }
          return isValid;
        })
        .map(action => ({
          id: action.id,
          actionTakenId: action.actionTakenId || null,
          description: action.description.trim(),
          startedAt: action.startedAt || null
        }));
      
      // Filter and map complaintActions (must have at least description)
      complaintActions = complaintActions
        .filter(action => {
          const isValid = action && 
            action.description && 
            typeof action.description === 'string' &&
            action.description.trim() !== '';
          if (!isValid) {
            console.warn('⚠️ Filtering out invalid action:', action);
          }
          return isValid;
        })
        .map(action => ({
          id: action.id,
          actionTypeId: action.actionTypeId || null,
          description: action.description.trim(),
          startedAt: action.startedAt || null,
          responsible: action.responsible || null,
          dueDate: action.dueDate || null,
          closeDate: action.closeDate || null
        }));
      
      // Handle products - similar to complaintActionTaken
      let products = formData.products || [];
      
      // Force sync products if available
      if (typeof window !== 'undefined' && window.__syncComplaintProducts) {
        window.__syncComplaintProducts();
        const syncData = window.__getComplaintProducts();
        if (syncData && syncData.localProducts && Array.isArray(syncData.localProducts)) {
          console.log('🔄 Got products from sync function:', syncData.localProducts);
          products = syncData.localProducts;
        }
      }
      
      console.log('🔍 STEP 1 - Raw products from formData:', products);
      console.log('🔍 STEP 1 - Is array?', Array.isArray(products));
      
      // Ensure it's an array
      if (!Array.isArray(products)) {
        console.warn('⚠️ products is not an array, converting to empty array');
        products = [];
      }
      
      // Filter out any invalid entries (products can be empty, but if they exist, they should have at least productCode or productName)
      products = products
        .filter(product => {
          // Products are optional, so we allow empty products but filter out completely empty ones
          const hasData = product && (
            (product.productCode && product.productCode.trim() !== '') ||
            (product.productName && product.productName.trim() !== '') ||
            (product.batchNumber && product.batchNumber.trim() !== '')
          );
          if (!hasData && product) {
            console.warn('⚠️ Filtering out invalid product (no data):', product);
          }
          return hasData;
        })
        .map(product => ({
          productCode: product.productCode?.trim() || null,
          productName: product.productName?.trim() || null,
          batchNumber: product.batchNumber?.trim() || null,
          productQuantity: product.productQuantity ? parseInt(product.productQuantity) : null,
          expiryDate: product.expiryDate || null
        }));
      
      console.log('🔍 STEP 3 - After filtering and mapping - products:', products);
      console.log('🔍 STEP 3 - After filtering and mapping - products length:', products.length);
      
      // Debug logging
      console.log('🔍 STEP 3 - After filtering and mapping - complaintActionTaken:', complaintActionTaken);
      console.log('🔍 STEP 3 - After filtering and mapping - complaintActionTaken length:', complaintActionTaken.length);
      console.log('🔍 STEP 3 - After filtering and mapping - complaintActions:', complaintActions);
      console.log('🔍 STEP 3 - After filtering and mapping - complaintActions length:', complaintActions.length);
      console.log('🔍 STEP 3 - formData keys:', Object.keys(formData));
      
      // Replicate the full handleSave logic from GenericModulePage
      // STEP 1: Validate data
      if (initializedConfig.validateData && typeof initializedConfig.validateData === 'function') {
        const validationResult = await initializedConfig.validateData(formData);
        if (!validationResult.isValid) {
          const errorMessage = Object.values(validationResult.errors).join(', ');
          throw new Error(`Validation failed: ${errorMessage}`);
        }
      }
      
      // STEP 2: Clean data (replicate GenericModulePage logic)
      const systemFieldsToRemove = ['id', 'createdAt', 'updatedAt', '_count'];
      const fieldsToKeep = initializedConfig.fieldsToKeep || [];
      const filteredSystemFieldsToRemove = systemFieldsToRemove.filter(field => !fieldsToKeep.includes(field));
      
      // Get lookup endpoints for relation fields
      const lookupEndpoints = initializedConfig.api?.lookupEndpoints || {};
      const relationFieldsToRemove = [];
      Object.keys(lookupEndpoints).forEach(key => {
        const fieldName = key.replace(/s$/, '');
        relationFieldsToRemove.push(fieldName);
      });
      
      const fieldsToRemove = [
        ...filteredSystemFieldsToRemove, 
        ...relationFieldsToRemove, 
        ...(initializedConfig.fieldsToRemove || [])
      ];
      
      const cleanedData = { ...formData };
      
      // Remove explicitly listed fields
      fieldsToRemove.forEach(field => {
        if (cleanedData.hasOwnProperty(field)) {
          delete cleanedData[field];
        }
      });
      
      // Remove object fields (likely relations) - but preserve arrays
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] && 
            typeof cleanedData[key] === 'object' && 
            !Array.isArray(cleanedData[key]) && 
            cleanedData[key].constructor === Object) {
          delete cleanedData[key];
        }
      });
      
      // Convert string IDs to numbers (handles both 'Id' and 'Ids' endings)
      Object.keys(cleanedData).forEach(key => {
        if (key.endsWith('Id') || key.endsWith('Ids')) {
          if (key === 'involvedDepartmentsIds' || key === 'forwardToIds') {
            // Special handling for array of IDs
            if (Array.isArray(cleanedData[key])) {
              const numericArray = cleanedData[key]
                .map(id => {
                  if (typeof id === 'string') {
                    const num = Number(id);
                    return isNaN(num) ? null : num;
                  }
                  if (typeof id === 'number') {
                    return isNaN(id) ? null : id;
                  }
                  return null;
                })
                .filter(id => id !== null);
              cleanedData[key] = numericArray;
            } else if (cleanedData[key] === '' || cleanedData[key] === null || cleanedData[key] === undefined) {
              cleanedData[key] = [];
            }
          } else {
            // Regular single ID field - send null when empty so backend can clear
            if (cleanedData[key] === '' || cleanedData[key] === null || cleanedData[key] === undefined) {
              cleanedData[key] = null;
            } else {
              const numValue = Number(cleanedData[key]);
              if (!isNaN(numValue)) {
                cleanedData[key] = numValue;
              } else {
                cleanedData[key] = null;
              }
            }
          }
        }
      });
      
      // Apply transformData (this will remove complaintActionTaken)
      const transformedData = initializedConfig.transformData
        ? initializedConfig.transformData(cleanedData, formMode) 
        : cleanedData;
      
      // Add products to transformedData (products are handled by the backend directly)
      if (products && products.length > 0) {
        transformedData.products = products;
      } else {
        // If products array is empty, send empty array to clear existing products
        transformedData.products = [];
      }
      
      // Debug: Log involvedDepartmentsIds to verify it's an array of numbers
      if (transformedData.involvedDepartmentsIds !== undefined) {
        console.log('🔍 involvedDepartmentsIds before API call:', {
          value: transformedData.involvedDepartmentsIds,
          type: typeof transformedData.involvedDepartmentsIds,
          isArray: Array.isArray(transformedData.involvedDepartmentsIds),
          firstElementType: Array.isArray(transformedData.involvedDepartmentsIds) 
            ? typeof transformedData.involvedDepartmentsIds[0] 
            : 'N/A'
        });
      }
      
      // Debug: Log products to verify it's an array
      if (transformedData.products !== undefined) {
        console.log('🔍 products before API call:', {
          value: transformedData.products,
          type: typeof transformedData.products,
          isArray: Array.isArray(transformedData.products),
          length: transformedData.products.length
        });
      }
      
      // Save the complaint
      let savedComplaint;
      if (formMode === 'add') {
        savedComplaint = await createEntity(transformedData);
      } else {
        savedComplaint = await updateEntity(formData.id, transformedData);
      }
      
      // Refresh data
      await mutate();
      
      // Debug logging
      console.log('🔍 After save - savedComplaint:', savedComplaint);
      console.log('🔍 After save - savedComplaint.id:', savedComplaint?.id);
      console.log('🔍 After save - complaintActionTaken:', complaintActionTaken);
      console.log('🔍 After save - complaintActionTaken.length:', complaintActionTaken?.length || 0);
      console.log('🔍 After save - Condition check:', {
        hasSavedComplaint: !!savedComplaint,
        hasId: !!(savedComplaint?.id),
        hasComplaintActionTaken: !!complaintActionTaken,
        hasLength: !!(complaintActionTaken && complaintActionTaken.length > 0),
        willExecute: !!(savedComplaint && savedComplaint.id && complaintActionTaken && complaintActionTaken.length > 0)
      });
      
      // If complaint was saved successfully, handle ComplaintActionTaken (create/update/delete)
      if (savedComplaint && savedComplaint.id) {
        const complaintId = savedComplaint.id;

        // Fetch existing actions taken for this complaint using SWR cache
        const existingActionsTaken = await fetchActionsTakenByComplaint(complaintId);

        const existingIds = new Set(existingActionsTaken.map(a => a.id).filter(id => id && !id.toString().startsWith('temp-')));
        
        // If we have actions taken in the form, handle create/update
        if (complaintActionTaken && complaintActionTaken.length > 0) {
          console.log('✅ Condition met - proceeding to save complaintActionTaken items');
          console.log('🔍 About to save complaintActionTaken items:', {
            complaintId,
            itemsToSave: complaintActionTaken.length,
            items: complaintActionTaken
          });

          // Process each action taken
          let savedCount = 0;
          let errorCount = 0;
          const errors = [];
          
          for (const actionTaken of complaintActionTaken) {
            try {
              // Validate that description is not empty (required field)
              if (!actionTaken.description || actionTaken.description.trim() === '') {
                console.warn('⚠️ Skipping action taken without description:', actionTaken);
                errorCount++;
                errors.push({ action: actionTaken, reason: 'Missing description' });
                continue;
              }
              
              const actionTakenData = {
                complaintId: complaintId,
                actionTakenId: actionTaken.actionTakenId || null,
                description: actionTaken.description.trim(),
                startedAt: actionTaken.startedAt || null
                // dueDate and closeDate were removed from the backend schema/DTO,
                // so we no longer send them to avoid validation errors
              };
              
              console.log('📤 Sending actionTakenData to backend:', JSON.stringify(actionTakenData, null, 2));
              
              // Check if this is a new action or an update
              const isNew = !actionTaken.id || actionTaken.id.toString().startsWith('temp-') || !existingIds.has(actionTaken.id);
              
              if (isNew) {
                // Create new action taken using SWR hook
                console.log('🔄 Creating new ComplaintActionTaken using SWR:', actionTakenData);
                try {
                  const savedAction = await createActionTaken(actionTakenData);
                  console.log('✅ Successfully created ComplaintActionTaken:', savedAction);
                  savedCount++;
                  // Invalidate cache to refresh the list
                  await mutateActionTaken();
                  await swrMutate(`/complaint-action-taken/complaint/${complaintId}`);
                } catch (error) {
                  console.error(`❌ Failed to create ComplaintActionTaken:`, {
                    error: error.message,
                    info: error.info,
                    status: error.status,
                    data: actionTakenData
                  });
                  errorCount++;
                  errors.push({ action: actionTakenData, error: error.message, status: error.status });
                  // Don't throw - continue processing other items, but collect errors
                }
              } else {
                // Update existing ComplaintActionTaken using SWR hook
                console.log('🔄 Updating existing ComplaintActionTaken using SWR:', actionTaken.id, actionTakenData);
                try {
                  const updatedAction = await updateActionTaken(actionTaken.id, actionTakenData);
                  console.log('✅ Successfully updated ComplaintActionTaken:', updatedAction);
                  savedCount++;
                  // Invalidate cache to refresh the list
                  await mutateActionTaken();
                  await swrMutate(`/complaint-action-taken/complaint/${complaintId}`);
                } catch (error) {
                  console.error(`❌ Failed to update ComplaintActionTaken:`, {
                    error: error.message,
                    info: error.info,
                    status: error.status,
                    data: actionTakenData
                  });
                  errorCount++;
                  errors.push({ action: actionTakenData, error: error.message, status: error.status });
                  // Don't throw - continue processing other items, but collect errors
                }
              }
            } catch (error) {
              console.error('❌ Unexpected error saving action taken:', error);
              errorCount++;
              errors.push({ action: actionTaken, error: error.message });
            }
          }
          
          // Log summary
          console.log('📊 Action Taken Save Summary:', {
            total: complaintActionTaken.length,
            saved: savedCount,
            errorCount: errorCount,
            errors: errors
          });
          
          // If there were errors, show a warning but don't fail the entire save
          if (errorCount > 0) {
            console.warn('⚠️ Some action taken records failed to save:', errors);
          }
        } else {
          console.warn('⚠️ No complaintActionTaken items to create/update; will only process deletions if needed.');
        }
        
        // Always process deletions: actions taken that were removed (exist in DB but not in formData)
        for (const existingAction of existingActionsTaken) {
          const stillExists = complaintActionTaken.some(
            a => a.id === existingAction.id || 
            (a.id && existingAction.id && a.id.toString() === existingAction.id.toString())
          );
          
          if (!stillExists && existingAction.id && !existingAction.id.toString().startsWith('temp-')) {
            try {
              console.log('🔄 Deleting removed action taken using SWR:', existingAction.id);
              await deleteActionTaken(existingAction.id);
              console.log('✅ Successfully deleted action taken:', existingAction.id);
              // Invalidate caches to refresh the lists
              await mutateActionTaken(); // invalidate /complaint-action-taken list
              await swrMutate(`/complaint-action-taken/complaint/${complaintId}`); // invalidate complaint-specific list
            } catch (error) {
              console.error(`❌ Failed to delete action taken:`, {
                error: error.message,
                info: error.info
              });
            }
          }
        }
      } else {
        console.warn('⚠️ Condition NOT met - NOT saving complaintActionTaken items. Reasons:', {
          hasSavedComplaint: !!savedComplaint,
          hasId: !!(savedComplaint?.id),
          savedComplaintId: savedComplaint?.id,
          hasComplaintActionTaken: !!complaintActionTaken,
          complaintActionTakenLength: complaintActionTaken?.length || 0,
          complaintActionTaken: complaintActionTaken
        });
      }
      
      // Save ComplaintActions if complaint was saved successfully and we have actions to save
      if (savedComplaint && savedComplaint.id && complaintActions && complaintActions.length > 0) {
        console.log('✅ Condition met - proceeding to save complaintActions items');
        const complaintId = savedComplaint.id;
        
        console.log('🔍 About to save complaintActions items:', {
          complaintId,
          itemsToSave: complaintActions.length,
          items: complaintActions
        });
        
        // Fetch existing actions for this complaint using SWR cache
        const existingActions = await fetchActionsByComplaint(complaintId);
        
        const existingActionIds = new Set(existingActions.map(a => a.id).filter(id => id && !id.toString().startsWith('temp-')));
        
        // Process each action
        let savedActionCount = 0;
        let errorActionCount = 0;
        const actionErrors = [];
        
        for (const action of complaintActions) {
          try {
            // Validate that description is not empty (required field)
            if (!action.description || action.description.trim() === '') {
              console.warn('⚠️ Skipping action without description:', action);
              errorActionCount++;
              actionErrors.push({ action: action, reason: 'Missing description' });
              continue;
            }
            
            const actionData = {
              complaintId: complaintId,
              actionTypeId: action.actionTypeId || null,
              description: action.description.trim(),
              startedAt: action.startedAt || null,
              responsible: action.responsible || null,
              dueDate: action.dueDate || null,
              closeDate: action.closeDate || null
            };
            
            console.log('📤 Sending actionData to backend:', JSON.stringify(actionData, null, 2));
            
            // Check if this is a new action or an update
            const isNew = !action.id || action.id.toString().startsWith('temp-') || !existingActionIds.has(action.id);
            
            if (isNew) {
              // Create new action using SWR hook
              console.log('🔄 Creating new ComplaintAction using SWR:', actionData);
              try {
                const savedAction = await createAction(actionData);
                console.log('✅ Successfully created ComplaintAction:', savedAction);
                savedActionCount++;
                // Invalidate cache to refresh the list
                await mutateAction();
                await swrMutate(`/complaint-action/complaint/${complaintId}`);
              } catch (error) {
                console.error(`❌ Failed to create ComplaintAction:`, {
                  error: error.message,
                  info: error.info,
                  status: error.status,
                  data: actionData
                });
                errorActionCount++;
                actionErrors.push({ action: actionData, error: error.message, status: error.status });
              }
            } else {
              // Update existing ComplaintAction using SWR hook
              console.log('🔄 Updating existing ComplaintAction using SWR:', action.id, actionData);
              try {
                const updatedAction = await updateAction(action.id, actionData);
                console.log('✅ Successfully updated ComplaintAction:', updatedAction);
                savedActionCount++;
                // Invalidate cache to refresh the list
                await mutateAction();
                await swrMutate(`/complaint-action/complaint/${complaintId}`);
              } catch (error) {
                console.error(`❌ Failed to update ComplaintAction:`, {
                  error: error.message,
                  info: error.info,
                  status: error.status,
                  data: actionData
                });
                errorActionCount++;
                actionErrors.push({ action: actionData, error: error.message, status: error.status });
              }
            }
          } catch (error) {
            console.error('❌ Unexpected error saving action:', error);
            errorActionCount++;
            actionErrors.push({ action: action, error: error.message });
          }
        }
        
        // Log summary
        console.log('📊 Action Save Summary:', {
          total: complaintActions.length,
          saved: savedActionCount,
          errorCount: errorActionCount,
          errors: actionErrors
        });
        
        // If there were errors, show a warning but don't fail the entire save
        if (errorActionCount > 0) {
          console.warn('⚠️ Some action records failed to save:', actionErrors);
        }
        
        // Delete actions that were removed (exist in DB but not in formData)
        for (const existingAction of existingActions) {
          const stillExists = complaintActions.some(
            a => a.id === existingAction.id || 
            (a.id && existingAction.id && a.id.toString() === existingAction.id.toString())
          );
          
          if (!stillExists && existingAction.id && !existingAction.id.toString().startsWith('temp-')) {
            try {
              console.log('🔄 Deleting removed action using SWR:', existingAction.id);
              await deleteAction(existingAction.id);
              console.log('✅ Successfully deleted action:', existingAction.id);
              // Invalidate cache to refresh the list
              await mutateAction();
              await swrMutate(`/complaint-action/complaint/${complaintId}`);
            } catch (error) {
              console.error(`❌ Failed to delete action:`, {
                error: error.message,
                info: error.info
              });
            }
          }
        }
      } else if (complaintActions && complaintActions.length > 0) {
        console.warn('⚠️ Condition NOT met - NOT saving complaintActions items. Reasons:', {
          hasSavedComplaint: !!savedComplaint,
          hasId: !!(savedComplaint?.id),
          savedComplaintId: savedComplaint?.id,
          hasComplaintActions: !!complaintActions,
          complaintActionsLength: complaintActions?.length || 0,
          complaintActions: complaintActions
        });
      }
      
      // After saving all actionTaken and Actions, refresh the complaint list cache
      // This ensures the table shows the updated data immediately
      await mutate();
      
      return savedComplaint;
    } catch (error) {
      console.error('❌ Save error:', error);
      throw error;
    }
  };
  
  // Handle type selection
  const handleTypeSelected = (typeId, typeData) => {
    setShowTypeSelector(false);
    
    // Store the selected type - we'll use a custom handler to set it when form opens
    // This will be handled by a custom handleAdd function
    window.__pendingComplaintTypeId = typeId;
    
    // Trigger the form to open by dispatching a custom event
    // The GenericFormsPage will listen for this and open the form
    window.dispatchEvent(new CustomEvent('complaint:openFormWithType', { 
      detail: { typeId, typeData } 
    }));
  };

  // Pass custom onSave and custom handleAdd to config
  const configWithCustomSave = {
    ...initializedConfig,
    onSave: customOnSave,
    // Add custom handleAdd that shows type selector first
    customHandleAdd: () => {
      setShowTypeSelector(true);
    }
  };
  
  return (
    <>
      <GenericModulePage config={configWithCustomSave} />
      <ComplaintTypeSelector
        isOpen={showTypeSelector}
        onClose={() => setShowTypeSelector(false)}
        onSelectType={handleTypeSelected}
        config={initializedConfig}
      />
    </>
  );
};

ComplaintPage.displayName = 'ComplaintPage';

export default ComplaintPage;
